import { useState, useMemo, useEffect } from 'react';
import PhoneFilters from '../components/PhoneFilters';
import { useTableBankingLoans, useCreateTableBankingLoan, useUpdateTableBankingLoan, useDeleteTableBankingLoan, useExportTableBankingLoans } from '../hooks/useTableBankingLoans.ts';
import Table from '../components/Table.tsx';
import Modal from '../components/Modal.tsx';
import LoanFormWithDocuments from '../components/LoanFormWithDocuments';
import { useDebounce } from '../hooks/useDebounce.ts';
import { toast } from 'react-hot-toast';
import { useMembers } from '../hooks/useMembers.ts';
import TableBankingLoanForm from '../components/TableBankingForm.tsx';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { Pencil, Trash2, Eye, Check, X, Download, Filter, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios.ts';
import { useQueryClient } from '@tanstack/react-query';
import getApiErrorMessage from '../utils/helper.js';
import { formatCurrency } from '../hooks/useLoanRepayments.ts';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import ExportFormatModal from '../components/ExportFormatModal.tsx';
import Select from '../components/ui/Select.tsx';

const tableBankingStatusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'disapproved', label: 'Disapproved' },
  { value: 'active', label: 'Active (disbursed)' },
];

/** Laravel JsonResource::collection() nested in json() can yield { data: { ...attrs } } per row — merge so id is on the row root. */
function flattenTableBankingLoanRow(row) {
  if (!row || typeof row !== 'object') return row;
  const inner = row.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return { ...row, ...inner };
  }
  return row;
}

export default function TableBankingLoansSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [editingLoanId, setEditingLoanId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null); // react-select option { value, label } | null
  const [page, setPage] = useState(1);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [selectedLoanForApproval, setSelectedLoanForApproval] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'disapprove'
  const [approvalNotes, setApprovalNotes] = useState('');
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const { data: membersData } = useMembers();
  const isAdmin = useIsAdmin();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  

   // Approval workflow functions
  const handleApprovalAction = (loan, action) => {
    setSelectedLoanForApproval(loan);
    setApprovalAction(action);
    setApprovalModalOpen(true);
    setApprovalNotes('');
  };

  const handleDeleteLoan = (id) => {
    if (window.confirm('Are you sure you want to delete this loan?')) {
      deleteLoan.mutate(id, {
        onSuccess: () => {
          // Invalidate loan queries
          queryClient.invalidateQueries({ queryKey: ['tableBankingLoans'] });
        }
      });
    }
  };


  const submitApproval = async () => {
    if (!selectedLoanForApproval || !approvalAction) return;

    const flat = flattenTableBankingLoanRow(selectedLoanForApproval);
    const loanId = Number(flat.id);
    if (!Number.isFinite(loanId) || loanId < 1) {
      toast.error('Invalid loan selected. Please close the dialog and try again.');
      return;
    }

    setIsApprovalLoading(true);
    try {
      const endpoint =
        approvalAction === 'approve'
          ? `/tablebanking/approve/${loanId}`
          : `/tablebanking/disapprove/${loanId}`;
      const payload = {
        reason: approvalNotes,
        send_sms: true,
      };

      await api.post(endpoint, payload);
      
      toast.success(`Loan ${approvalAction === 'approve' ? 'approved' : 'disapproved'} successfully`);
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['tableBankingLoans'] });
      
      setApprovalModalOpen(false);
      setSelectedLoanForApproval(null);
      setApprovalAction(null);
      setApprovalNotes('');
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      toast.error(errorMessage || `Failed to ${approvalAction} loan`);
    } finally {
      setIsApprovalLoading(false);
    }
  };


  const debouncedSearch = useDebounce(searchQuery, 500);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const filters = {
    search: debouncedSearch,
    status: statusFilter?.value,
    page,
    per_page: 15,
  };

  const exportFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      status: statusFilter?.value || undefined,
    }),
    [debouncedSearch, statusFilter?.value]
  );

  const { data: tbkLoansData, isLoading } = useTableBankingLoans(filters);
  const exportTableBankingLoans = useExportTableBankingLoans();
  const createLoan = useCreateTableBankingLoan();
  const updateLoan = useUpdateTableBankingLoan();
  const deleteLoan = useDeleteTableBankingLoan();
  
  const memberOptions = membersData?.data?.map(member => ({
    value: member.id,
    label: member.name,
  })) || [];
  const handleCreate = async (loanData) => {
    try {
      await createLoan.mutateAsync(loanData);
      setIsModalOpen(false);
    } catch (err) {
      toast.error('Failed to create Table Banking Loan');
    }
  };
  const handleUpdate = async (loanData) => {
    try {
      await updateLoan.mutateAsync({ id: selectedLoan.id, loan: loanData });
      setIsModalOpen(false);
    } catch (err) {
      toast.error('Failed to create Table Banking Loan');
    }
  };

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_ALL_LOANS}>
      <div className="space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Table Banking Loans</h2>
          {can(PERMISSIONS.CREATE_LOAN) && (
            <button
              type="button"
              onClick={() => {
                setSelectedLoan(null);
                setEditingLoanId(null);
                setIsModalOpen(true);
              }}
              className="inline-flex items-center justify-center font-medium bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              + New Loan
            </button>
          )}
        </div>

      {/* Same card pattern as Member Loans so filters stay visible in light/dark theme */}
      <PhoneFilters count={(statusFilter ? 1 : 0) + (searchQuery ? 1 : 0)}>
<div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-600 dark:text-gray-400 shrink-0" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
          </div>
          <button
            type="button"
            className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            onClick={() => {
              setSearchQuery('');
              setStatusFilter(null);
              setPage(1);
            }}
          >
            Clear all
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search by member
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Name or phone…"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search size={18} className="absolute left-3 top-2.5 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <Select
              options={tableBankingStatusOptions}
              value={statusFilter}
              onChange={(option) => {
                setStatusFilter(option);
                setPage(1);
              }}
              isClearable
              placeholder="All statuses"
              classNamePrefix="select"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Report
            </span>
            <button
              type="button"
              onClick={() => setShowExportModal(true)}
              disabled={exportTableBankingLoans.isPending}
              className="inline-flex items-center justify-center font-medium border-2 border-blue-600 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 px-4 py-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50 min-h-[42px]"
            >
              <Download className="w-4 h-4 mr-2 shrink-0" />
              {exportTableBankingLoans.isPending ? 'Exporting…' : 'Export report'}
            </button>
          </div>
        </div>
      </div>
      </PhoneFilters>

      {/* Loans Table */}
      <Table
        data={(tbkLoansData?.data?.data || []).map(flattenTableBankingLoanRow)}
        isLoading={isLoading}
       
       onPageChange={setPage}

        columns={[
          { label: 'Member', field: 'member', sortable: true, render:  (row) => row.member?.name || '-' },
          { label: 'Principal Amount',field: 'principal_amount',  render: row => row.principal_amount || '-'  },
          { label: 'Total Amount', field: 'total_amount', render: row=> row.total_amount || '-' },
          { label: 'Status', accessor: 'status' , sortable: true, field: 'status', render: row=> row.status || '-'},
           {
      label: 'Actions',
      field: 'actions',
      render: (row) => (
        <div className="flex space-x-2">
          <button 
            className="text-green-600 hover:text-green-800" 
            onClick={() => navigate(`/table-loan-details/${row.id}`)}
            title="View Details"
          >
            <Eye size={18} />
              {/* <Check size={18} /> */}
          </button>
          
          {/* Approval workflow actions for admins */}
          {isAdmin && row.status === 'pending' && (
            <>
              {can(PERMISSIONS.APPROVE_LOAN) && (
                <button 
                  className="text-green-600 hover:text-green-800" 
                  onClick={() => handleApprovalAction(row, 'approve')}
                  title="Approve Loan"
                >
                  <Check size={18} />
                </button>
              )}
              {can(PERMISSIONS.REJECT_LOAN) && (
                <button 
                  className="text-red-600 hover:text-red-800" 
                  onClick={() => handleApprovalAction(row, 'disapprove')}
                  title="Disapprove Loan"
                >
                  <X size={18} />
                </button>
              )}
            </>
          )}
          
          
          {isAdmin && (
            <>
              {can(PERMISSIONS.EDIT_LOAN) && (
                <button 
                  className="text-blue-600 hover:text-blue-800" 
                  onClick={() => { 
                    setEditingLoanId(row.id);
                    setSelectedLoan(row); 
                    setIsModalOpen(true); 
                  }}
                  title="Edit"
                >
                  <Pencil size={18} />
                </button>
              )}
              {can(PERMISSIONS.DELETE_LOAN) && (
                <button 
                  className="text-red-600 hover:text-red-800" 
                  onClick={() => handleDeleteLoan(row.id)}
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </>
          )}
        </div>
      )
    },
        ]}
        onEdit={(row) => {
          setEditingLoanId(row.id);
          setSelectedLoan(row);
          setIsModalOpen(true);
        }}
        onDelete={(row) => deleteLoan.mutate(row.id)}
       

      />
    {/* Pagination Controls */}
{tbkLoansData?.data?.meta && (
  <div className="flex justify-between items-center mt-4">
    <span className="text-sm text-gray-600">
      Showing {tbkLoansData.data.meta.from || 0}–{tbkLoansData.data.meta.to || 0} of {tbkLoansData.data.meta.total} results
    </span>

    <div className="flex items-center space-x-2">
      <button
        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
        disabled={page <= 1}
        className={`px-3 py-1 border rounded ${page <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        Previous
      </button>
      <span className="text-sm text-gray-700">
        Page {tbkLoansData.data.meta.current_page} of {tbkLoansData.data.meta.last_page}
      </span>
      <button
        onClick={() => setPage((prev) => Math.min(prev + 1, tbkLoansData.data.meta.last_page))}
        disabled={page >= tbkLoansData.data.meta.last_page}
        className={`px-3 py-1 border rounded ${page >= tbkLoansData.data.meta.last_page ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        Next
      </button>
    </div>
  </div>
)}



      {/* Modal for create/edit */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <TableBankingLoanForm
          loan={selectedLoan}
          onSubmit={editingLoanId ? handleUpdate : handleCreate}
          onClose={() => setIsModalOpen(false)}
          members={membersData?.data || []}

        />
      </Modal>



       {/* Approval Modal */}
      <Modal
        isOpen={approvalModalOpen}
        onClose={() => setApprovalModalOpen(false)}
        title={`${approvalAction === 'approve' ? 'Approve' : 'Disapprove'} Loan`}
      >
        <div className="space-y-4">
          {selectedLoanForApproval && (() => {
            const loan = flattenTableBankingLoanRow(selectedLoanForApproval);
            return (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Loan Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Loan ID:</span>
                  <p className="font-medium">{loan.id}</p>
                </div>
                <div>
                  <span className="text-gray-600">Member:</span>
                  <p className="font-medium">{loan.member?.name}</p>
                </div>
                <div>
                  <span className="text-gray-600">Amount:</span>
                  <p className="font-medium">{formatCurrency(loan.principal_amount)}</p>
                </div>
              </div>
            </div>
            );
          })()}
          
          <p className="text-sm text-gray-700">
            Are you sure you want to <strong>{approvalAction}</strong> this loan? This action cannot be undone.
          </p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              rows="4"
              placeholder="Add any notes for the approval/disapproval..."
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
              onClick={() => setApprovalModalOpen(false)}
              disabled={isApprovalLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded text-white hover:opacity-90 ${
                approvalAction === 'approve' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              onClick={submitApproval}
              disabled={isApprovalLoading}
            >
              {isApprovalLoading ? 'Processing...' : `${approvalAction === 'approve' ? 'Approve' : 'Disapprove'} Loan`}
            </button>
          </div>
        </div>
      </Modal>

      <ExportFormatModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={(format) => exportTableBankingLoans.mutate({ filters: exportFilters, format })}
        title="Export table banking loans"
        subtitle="Uses the same search and status filters as the list (all matching rows, not only this page)."
      />

    </div>
    </PermissionPage>
  );
}
